import React, { useState, useEffect } from 'react';
import { ArrowRight, Play, CheckCircle, Zap, Globe, Shield } from 'lucide-react';

const HeroSection = () => {
    const [isVisible, setIsVisible] = useState(false);
    const [currentWordIndex, setCurrentWordIndex] = useState(0);
    const [currentWord, setCurrentWord] = useState('');
    const [isTyping, setIsTyping] = useState(true);
    const [showCursor, setShowCursor] = useState(true);
    const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

    const words = ['EFFICIENCY', 'INNOVATION', 'RELIABILITY', 'TRANSPARENCY'];
    const typingSpeed = 100;
    const eraseSpeed = 60;
    const pauseDelay = 500;

    useEffect(() => {
        const timer = setTimeout(() => {
            setIsVisible(true);
        }, 100);
        return () => clearTimeout(timer);
    }, []);

    // Mouse tracking for parallax effect
    useEffect(() => {
        const handleMouseMove = (e) => {
            setMousePosition({
                x: (e.clientX / window.innerWidth) * 2 - 1,
                y: (e.clientY / window.innerHeight) * 2 - 1
            });
        };

        window.addEventListener('mousemove', handleMouseMove);
        return () => window.removeEventListener('mousemove', handleMouseMove);
    }, []);

    // Cursor blinking effect
    useEffect(() => {
        const cursorInterval = setInterval(() => {
            setShowCursor(prev => !prev);
        }, 600);
        return () => clearInterval(cursorInterval);
    }, []);

    // Typewriter animation
    useEffect(() => {
        let timeout;
        const word = words[currentWordIndex];

        if (isTyping) {
            if (currentWord.length < word.length) {
                timeout = setTimeout(() => {
                    setCurrentWord(word.substring(0, currentWord.length + 1));
                }, typingSpeed);
            } else {
                timeout = setTimeout(() => {
                    setIsTyping(false);
                }, pauseDelay);
            }
        } else {
            if (currentWord.length > 0) {
                timeout = setTimeout(() => {
                    setCurrentWord(currentWord.substring(0, currentWord.length - 1));
                }, eraseSpeed);
            } else {
                setCurrentWordIndex((prev) => (prev + 1) % words.length);
                setIsTyping(true);
            }
        }

        return () => clearTimeout(timeout);
    }, [currentWord, currentWordIndex, isTyping, words, typingSpeed, eraseSpeed, pauseDelay]);

    const features = [
        { icon: Zap, text: "Instant Tracking", color: "#10B981" },
        { icon: Shield, text: "Secure Transport", color: "#3B82F6" },
        { icon: Globe, text: "Pan-India Network", color: "#F59E0B" }
    ];

    return (
        <div className="relative w-full h-screen flex items-center justify-center overflow-hidden">
            {/* Background Video */}
            <video
                className="absolute inset-0 w-full h-full object-cover scale-105"
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                onError={(e) => {
                    console.error('Video failed to load:', e);
                    e.target.style.display = 'none';
                    e.target.nextElementSibling.style.display = 'block';
                }}
            >
                <source src="https://prod-roadexpress.s3.ap-south-1.amazonaws.com/website/homepage.mp4" type="video/mp4" />
                Your browser does not support the video tag.
            </video>

            {/* Dynamic overlay with parallax */}
            <div
                className="absolute inset-0 z-10"
                style={{
                    background: `radial-gradient(circle at ${50 + mousePosition.x * 10}% ${50 + mousePosition.y * 10}%, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.7) 100%)`
                }}
            ></div>
            {/* Main Content */}
            <div className="relative z-20 max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8">
                <div className="grid lg:grid-cols-12 gap-8 items-center">

                    {/* Left Content */}
                    <div className="lg:col-span-7">

                        {/* Main Heading */}
                        <div
                            className={`transform transition-all duration-1000 ease-out ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
                                }`}
                            style={{ transitionDelay: '0.2s' }}
                        >
                            <h1 className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold leading-[1.1] mb-6">
                                <span className="text-white block">
                                    Logistics built for
                                </span>
                                <span className="flex items-center flex-wrap">
                                    <span className="relative">
                                        <span className="text-[#F05A28]">{currentWord}</span>
                                        <span
                                            className={`inline-block w-1 h-[0.8em] ml-2 bg-[#F05A28] ${showCursor ? 'opacity-100' : 'opacity-0'
                                                } transition-opacity duration-200`}
                                        ></span>
                                    </span>
                                </span>
                            </h1>
                        </div>

                        {/* Description */}
                        <div
                            className={`transform transition-all duration-1000 ease-out ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
                                }`}
                            style={{ transitionDelay: '0.4s' }}
                        >
                            <p className="text-lg md:text-xl text-white/80 max-w-2xl leading-relaxed mb-10">
                                Transform your supply chain with intelligent routing, real-time visibility,
                                and seamless integration. Built for businesses that refuse to compromise.
                            </p>
                        </div>

                        {/* Features */}
                        <div
                            className={`transform transition-all duration-1000 ease-out ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
                                }`}
                            style={{ transitionDelay: '0.8s' }}
                        >
                            <div className="flex flex-wrap gap-6">
                                {features.map((feature, index) => {
                                    const IconComponent = feature.icon;
                                    return (
                                        <div
                                            key={index}
                                            className="flex items-center gap-3 bg-white/10 backdrop-blur-sm rounded-lg px-4 py-3 border border-white/20"
                                        >
                                            <div
                                                className="w-8 h-8 rounded-lg flex items-center justify-center"
                                                style={{ backgroundColor: `${feature.color}20` }}
                                            >
                                                <IconComponent
                                                    className="w-4 h-4"
                                                    style={{ color: feature.color }}
                                                />
                                            </div>
                                            <span className="text-white/90 font-medium text-sm">
                                                {feature.text}
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default HeroSection;